import { useEffect, useState } from "react";
import {
  ApiError,
  type CoverageGapsDto,
  type CoverageHealthDto,
  type CoverageResolutionDto,
  type ReclassificationRowDto,
  type SegmentGridRowDto,
  fetchCoverageGaps,
  fetchCoverageHealth,
  fetchCoverageReclassifications,
  fetchCoverageResolution,
  fetchCoverageSegmentGrid,
  fetchStocks,
} from "../api";
import { ConvictionBadge, ConvictionExplain } from "../components/ConvictionBadge";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";
import { exportRowsAsCsv } from "../exportCsv";
import { sortGlyph, useSort } from "../hooks/useSort";

type Tab = "health" | "grid" | "resolver" | "gaps" | "reclassifications";

export function CoveragePage({ token, onUnauthorized }: { token: string; onUnauthorized: () => void }) {
  const [tab, setTab] = useState<Tab>("health");
  const [error, setError] = useState<string | null>(null);
  function authAware(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) { onUnauthorized(); return true; }
    return false;
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Coverage</h1>
          <div className="page-sub">How segment models reach actual stocks. A read &amp; audit surface — signals are computed on the fly, membership is derived from data.</div>
        </div>
      </div>
      <PageHelper>
        A stock's signal is never stored — it's assembled on the fly from whichever segment models apply to it.
        This page audits that assembly: how much of the universe is touched (<FieldHelp term="stock_breadth" />
        breadth), how well each stock is served (<FieldHelp term="stock_depth" />depth), which segments have no
        model to build next, and a resolver to see exactly how any one stock's signal comes together.
      </PageHelper>
      {error && <div className="error" style={{ margin: "10px 0" }}>{error}</div>}
      <div className="levtabs" style={{ marginBottom: 16 }}>
        <button className={tab === "health" ? "on" : undefined} onClick={() => setTab("health")}>Health</button>
        <button className={tab === "grid" ? "on" : undefined} onClick={() => setTab("grid")}>Segment grid</button>
        <button className={tab === "resolver" ? "on" : undefined} onClick={() => setTab("resolver")}>Stock resolver</button>
        <button className={tab === "gaps" ? "on" : undefined} onClick={() => setTab("gaps")}>Gaps</button>
        <button className={tab === "reclassifications" ? "on" : undefined} onClick={() => setTab("reclassifications")}>Reclassifications</button>
      </div>
      {tab === "health" && <HealthTab token={token} authAware={authAware} setError={setError} onSeeGaps={() => setTab("gaps")} />}
      {tab === "grid" && <GridTab token={token} authAware={authAware} setError={setError} />}
      {tab === "resolver" && <ResolverTab token={token} authAware={authAware} setError={setError} />}
      {tab === "gaps" && <GapsTab token={token} authAware={authAware} setError={setError} />}
      {tab === "reclassifications" && <ReclassificationsTab token={token} authAware={authAware} setError={setError} />}
    </>
  );
}

type AuthAware = (err: unknown) => boolean;
type SetError = (e: string | null) => void;

// ---- HEALTH ----

function HealthTab({ token, authAware, setError, onSeeGaps }: { token: string; authAware: AuthAware; setError: SetError; onSeeGaps: () => void }) {
  const [health, setHealth] = useState<CoverageHealthDto | null>(null);

  useEffect(() => {
    fetchCoverageHealth(token).then(setHealth).catch((e) => { if (!authAware(e)) setError(e instanceof ApiError ? e.message : "failed to load health"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!health) return <div className="empty-state">Loading…</div>;

  const depthOrder = ["1", "2", "3", "4+"];
  const depthMax = Math.max(...depthOrder.map((k) => health.depth_distribution[k] ?? 0), 1);
  const thin = health.depth_distribution["1"] ?? 0;
  const rich = (health.depth_distribution["3"] ?? 0) + (health.depth_distribution["4+"] ?? 0);

  return (
    <div className="card">
      <div className="h3"><span>Universe coverage <span className="note">full active NSE + qualifying BSE · current membership</span></span><span className="note">as-of {health.as_of}</span></div>
      <div style={{ padding: 16, display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr", gap: 16 }}>
        <div>
          <div className="note" style={{ fontWeight: 700, marginBottom: 8 }}>Breadth — stocks touched by ≥1 model <FieldHelp term="stock_breadth" /></div>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "2.4rem", letterSpacing: "-0.03em" }}>
            {health.breadth_pct.toFixed(0)}<span style={{ fontSize: "1rem", color: "var(--ink-faint)" }}> % · {health.covered.toLocaleString()} of {health.universe_total.toLocaleString()}</span>
          </div>
          <div style={{ height: 14, borderRadius: 7, overflow: "hidden", display: "flex", margin: "12px 0 6px", border: "1px solid var(--rule-strong)" }}>
            <div style={{ width: `${health.breadth_pct}%`, background: "var(--ok)" }} />
            <div style={{ width: `${100 - health.breadth_pct}%`, background: "var(--bad)" }} />
          </div>
          <div className="note">{health.universe_total - health.covered === 0 ? "cap-tier models cover every stock — 0 stocks with no model at all" : `${(health.universe_total - health.covered).toLocaleString()} stocks with no model at all`}</div>
        </div>
        <div>
          <div className="note" style={{ fontWeight: 700, marginBottom: 8 }}>Depth — how many segments modelled <FieldHelp term="stock_depth" /></div>
          {depthOrder.map((k) => {
            const v = health.depth_distribution[k] ?? 0;
            return (
              <div key={k} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: "0.82rem", margin: "6px 0" }}>
                <span style={{ width: 90, flex: "none", color: "var(--ink-soft)" }}>{k} model{k === "1" ? "" : "s"}</span>
                <div style={{ flex: 1, height: 11, background: "var(--surface-2)", borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${(v / depthMax) * 100}%`, background: k === "1" ? "var(--warn)" : "var(--ok)", borderRadius: 3 }} />
                </div>
                <span className="mono" style={{ width: 60, textAlign: "right", color: "var(--ink-faint)", fontSize: "0.78rem" }}>{v.toLocaleString()}</span>
              </div>
            );
          })}
        </div>
        <div>
          <div className="note" style={{ fontWeight: 700, marginBottom: 8 }}>The honest gaps</div>
          <div style={{ fontSize: "0.86rem", lineHeight: 1.7 }}>
            <div><b className="mono" style={{ color: "var(--warn)" }}>{thin.toLocaleString()}</b> stocks are <b>thinly covered</b> (1 model only)</div>
            <div><b className="mono" style={{ color: "var(--ok)" }}>{rich.toLocaleString()}</b> are <b>richly covered</b> (3+ models)</div>
            <div><b className="mono" style={{ color: "var(--bad)" }}>{health.segments_not_modelled}</b> segments have <b>no model</b> yet — genuine build targets</div>
            {health.segments_tested_no_signal > 0 && (
              <div>
                <b className="mono" style={{ color: "var(--accent)" }}>{health.segments_tested_no_signal}</b> segments
                were <b>tested and confirmed no directional signal</b> <FieldHelp term="tested_no_signal" /> — resolved, not buildable
              </div>
            )}
            <button className="btn primary sm" style={{ marginTop: 10 }} onClick={onSeeGaps}>See what to build →</button>
          </div>
        </div>
      </div>
      <div className="foot-note">Cap-tier models blanket the universe (every stock has a cap), so breadth is near-complete — the real work is depth: stocks sitting in only one modelled segment want a second model to enrich them.</div>
    </div>
  );
}

// ---- SEGMENT GRID ----

function GridTab({ token, authAware, setError }: { token: string; authAware: AuthAware; setError: SetError }) {
  const [rows, setRows] = useState<SegmentGridRowDto[] | null>(null);
  useEffect(() => {
    fetchCoverageSegmentGrid(token).then(setRows).catch((e) => { if (!authAware(e)) setError(e instanceof ApiError ? e.message : "failed to load grid"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const { sorted, sortKey, sortDir, toggleSort } = useSort<SegmentGridRowDto>(
    rows ?? [], (row, key) => (row as unknown as Record<string, unknown>)[key],
  );

  if (!rows) return <div className="empty-state">Loading…</div>;

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="h3" style={{ padding: "16px 16px 0" }}>
        <span>Segment grid <span className="note">every dimension × segment · does a model exist, and how many stocks does it reach</span></span>
        <button className="btn ghost sm" onClick={() => exportRowsAsCsv(
          rows as unknown as Record<string, unknown>[],
          [
            { key: "dimension", label: "Dimension" }, { key: "segment_key", label: "Segment" },
            { key: "n_constituents", label: "Constituents" }, { key: "has_model", label: "Model built?" },
            { key: "model_id", label: "Model" }, { key: "stocks_covered", label: "Stocks covered" },
          ], "coverage_segment_grid.csv",
        )}>↓ CSV</button>
      </div>
      <table className="stocks-table">
        <thead>
          <tr>
            <th className="clickable" onClick={() => toggleSort("dimension")}>Dimension {sortGlyph("dimension", sortKey, sortDir)}</th>
            <th className="l clickable" onClick={() => toggleSort("label")}>Segment {sortGlyph("label", sortKey, sortDir)}</th>
            <th className="r clickable" onClick={() => toggleSort("n_constituents")}>Constituents {sortGlyph("n_constituents", sortKey, sortDir)}</th>
            <th className="clickable" onClick={() => toggleSort("has_model")}>Model built? {sortGlyph("has_model", sortKey, sortDir)}</th>
            <th className="l">Model</th>
            <th className="r clickable" onClick={() => toggleSort("stocks_covered")}>Stocks covered {sortGlyph("stocks_covered", sortKey, sortDir)}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr key={r.segment_key}>
              <td><span className="chip">{r.dimension}</span></td>
              <td className="l" style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>{r.label ?? r.segment_key}</td>
              <td className="r mono">{r.n_constituents.toLocaleString()}</td>
              <td>
                {r.has_model ? (
                  <span className="chip st-champion">✓ yes</span>
                ) : r.tested_no_signal ? (
                  <span className="chip" title={r.no_signal_reason ?? undefined} style={{ color: "var(--accent)", borderColor: "var(--accent-line)", background: "var(--accent-soft)" }}>
                    ◆ tested — no signal
                  </span>
                ) : (
                  <span className="chip st-challenger">✗ no model</span>
                )}
              </td>
              <td className="l mono" style={{ fontSize: "0.78rem" }}>{r.model_id ?? <span className="note">—</span>}{r.horizon ? ` (h=${r.horizon})` : ""}</td>
              <td className="r mono">{r.has_model ? r.stocks_covered.toLocaleString() : <span className="mut">0</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="foot-note">Rows with no model are the build targets — a segment with many constituents and no model is that many stocks getting less depth than they could.</div>
    </div>
  );
}

// ---- STOCK RESOLVER ----

function ResolverTab({ token, authAware, setError }: { token: string; authAware: AuthAware; setError: SetError }) {
  const [query, setQuery] = useState("");
  const [ret, setRet] = useState<CoverageResolutionDto | null>(null);
  const [vol, setVol] = useState<CoverageResolutionDto | null>(null);
  const [resolvedName, setResolvedName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);

  async function resolveStock() {
    const q = query.trim();
    if (!q) return;
    setBusy(true);
    setNotFound(false);
    setError(null);
    try {
      let securityId = q;
      // ISINs are 12 chars, always start "IN" -- anything else is treated as
      // a ticker and resolved via the real Stocks filter endpoint first.
      if (!/^IN[0-9A-Z]{10}$/i.test(q)) {
        const res = await fetchStocks(token, {
          groups: [{ op: "AND", conditions: [{ field: "display_ticker", operator: "eq", value: q.toUpperCase() }] }],
          group_op: "AND", include_signal: false, signal_output_type: "return", extra_columns: [],
          sort_key: null, sort_dir: "asc", limit: 1, offset: 0,
        });
        if (res.rows.length === 0) { setNotFound(true); setRet(null); setVol(null); setBusy(false); return; }
        securityId = res.rows[0].security_id;
        setResolvedName(res.rows[0].display_ticker ?? securityId);
      } else {
        setResolvedName(securityId);
      }
      const [r, v] = await Promise.all([
        fetchCoverageResolution(token, securityId, "return"),
        fetchCoverageResolution(token, securityId, "volatility"),
      ]);
      setRet(r);
      setVol(v);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) { setNotFound(true); setRet(null); setVol(null); }
      else if (!authAware(e)) setError(e instanceof ApiError ? e.message : "resolve failed");
    } finally {
      setBusy(false);
    }
  }

  const allSegmentKeys = ret
    ? Array.from(new Set([
        ...Object.keys(ret.contributing_segments),
        ...ret.gaps.map((g) => g.segment_key),
        ...ret.secondary_horizons.flatMap((h) => Object.keys(h.contributing_segments)),
      ]))
    : [];
  // TESTED-NO-SIGNAL, DISTINCT FROM A DEPTH GAP (2026-09-25): a segment
  // where a real build attempt confirmed no forecastable signal is a
  // RESOLVED negative, not something "building" would fix -- looked up per
  // segment_key so the membership row and the depth-gaps hint below can
  // both render it correctly instead of lumping it in with real gaps.
  const gapBySegment: Record<string, { reason: string; gap_type?: string }> = {};
  ret?.gaps.forEach((g) => { gapBySegment[g.segment_key] = g; });
  const realGaps = ret?.gaps.filter((g) => g.gap_type !== "tested_no_signal") ?? [];
  const testedNoSignalSegs = ret?.gaps.filter((g) => g.gap_type === "tested_no_signal") ?? [];
  // Which secondary horizon (if any) each segment_key feeds -- for the
  // membership list's "↔ has a model — different horizon (5d)" row.
  const secondaryHorizonBySegment: Record<string, number> = {};
  ret?.secondary_horizons.forEach((h) => {
    Object.keys(h.contributing_segments).forEach((seg) => { secondaryHorizonBySegment[seg] = h.horizon; });
  });

  return (
    <>
      <div className="card" style={{ padding: 16 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <input className="txt" style={{ flex: 1 }} placeholder="Search a stock — a ticker (SUNPHARMA) or a security_id (ISIN)"
            value={query} onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && resolveStock()} />
          <button className="btn primary" onClick={resolveStock} disabled={busy}>{busy ? "Resolving…" : "Resolve"}</button>
        </div>
        {notFound && <div className="note" style={{ marginTop: 10 }}>No stock found matching {JSON.stringify(query)} — try an exact ticker or paste the security_id from the Stocks page.</div>}
      </div>

      {ret && (
        <div className="card" style={{ padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.3rem" }}>{resolvedName}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <ConvictionBadge conviction={ret.conviction} />
              <span className={`chip ${allSegmentKeys.length >= 3 ? "st-champion" : ""}`}>
                {Object.keys(ret.contributing_segments).length} of {allSegmentKeys.length} segment{allSegmentKeys.length === 1 ? "" : "s"} have a model
              </span>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 16 }}>
            <div>
              <div className="note" style={{ fontWeight: 700, marginBottom: 8 }}>Memberships → which have a model</div>
              {allSegmentKeys.map((seg) => {
                const has = seg in ret.contributing_segments;
                // A segment at a DIFFERENT horizon than the primary blend is
                // no longer a hidden gap (2026-09-21) -- it feeds its own
                // secondary-horizon signal, shown below, not just a "gap".
                const secondaryH = secondaryHorizonBySegment[seg];
                const gap = gapBySegment[seg];
                const tested = gap?.gap_type === "tested_no_signal";
                return (
                  <div key={seg} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--rule)" }}>
                    <span style={{ width: 100, flex: "none", fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "0.8rem", color: "var(--ink-soft)" }}>{seg.split(":")[0]}</span>
                    <span className="mono" style={{ flex: 1, fontSize: "0.82rem" }}>{seg}</span>
                    {has ? (
                      <span className="chip st-champion">✓ model applies (primary)</span>
                    ) : secondaryH !== undefined ? (
                      <span className="chip" title={`Feeds the ${secondaryH}-day secondary signal below`}>
                        ↔ has a model — {secondaryH}-day view
                      </span>
                    ) : tested ? (
                      <span className="chip" title={gap?.reason} style={{ color: "var(--accent)", borderColor: "var(--accent-line)", background: "var(--accent-soft)" }}>
                        ◆ tested — no directional signal
                      </span>
                    ) : (
                      <span className="chip st-challenger">✗ no model — depth gap</span>
                    )}
                  </div>
                );
              })}
              {testedNoSignalSegs.length > 0 && (
                <div className="hint" style={{ marginTop: 8 }}>
                  <b>{testedNoSignalSegs.map((g) => g.segment_key.split(":")[0]).join(", ")}</b> {testedNoSignalSegs.length === 1 ? "was" : "were"} tested and confirmed to carry no directional signal —
                  a resolved finding, not something building a model would fix. See <FieldHelp term="tested_no_signal" />.
                </div>
              )}
              {realGaps.length > 0 && (
                <div className="hint" style={{ marginTop: 8 }}>
                  This stock's depth gaps: <b>{realGaps.map((g) => g.segment_key.split(":")[0]).join(", ")}</b> — building those segment models would enrich its blend.
                </div>
              )}
              {ret.secondary_horizons.length > 0 && (
                <div className="hint" style={{ marginTop: 8 }}>
                  {Object.values(secondaryHorizonBySegment).length > 0 && (
                    <>
                      <b>{Object.keys(secondaryHorizonBySegment).map((s) => s.split(":")[0]).join(", ")}</b>{" "}
                      already {Object.keys(secondaryHorizonBySegment).length === 1 ? "has" : "have"} a model, at a
                      different horizon than the primary blend — shown as its own clean secondary-horizon signal
                      below, not blended into the primary score (S10.13 Option-A: only same-horizon segments blend
                      together).
                    </>
                  )}
                </div>
              )}
            </div>
            <div>
              <div className="note" style={{ fontWeight: 700, marginBottom: 8 }}>Auto-assembled blend <span className="note">weighted</span></div>
              {Object.entries(ret.contributing_segments).map(([seg, c]) => (
                <div key={seg} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--rule)" }}>
                  <span style={{ flex: 1, fontSize: "0.82rem", fontFamily: "var(--font-display)", fontWeight: 600 }}>{c.model_id}</span>
                  <div style={{ width: 100, height: 10, background: "var(--surface-2)", borderRadius: 3, overflow: "hidden", flex: "none" }}>
                    <div style={{ height: "100%", width: `${c.weight * 100}%`, background: "var(--accent)" }} />
                  </div>
                  <span className="mono" style={{ width: 40, textAlign: "right", fontSize: "0.78rem", color: "var(--ink-faint)" }}>{(c.weight * 100).toFixed(0)}%</span>
                </div>
              ))}
              {ret.near_earnings && (
                <div style={{ marginTop: 6 }}><span className="gaps-badge">⚡ earnings overlay armed ({ret.days_since_earnings}d)</span></div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 16, background: "var(--surface-2)", border: "1px solid var(--rule)", borderRadius: 10, padding: "16px 18px", marginTop: 12 }}>
                <div>
                  <div className="note" style={{ fontFamily: "var(--font-display)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.6rem" }}>Return signal</div>
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.4rem", color: ret.signal === null ? "var(--ink-faint)" : ret.signal >= 0 ? "var(--ok)" : "var(--bad)" }}>
                    {ret.signal === null ? "no signal" : `${ret.signal >= 0 ? "+" : ""}${(ret.signal * 100).toFixed(2)}%`}
                  </div>
                  <div className="mono note">{ret.matched_horizon ? `${ret.matched_horizon}d horizon` : "—"}</div>
                </div>
                {vol && (
                  <div style={{ marginLeft: "auto", textAlign: "right" }}>
                    <div className="note">Vol σ <FieldHelp term="rank_space_sigma" /></div>
                    <div className="mono" style={{ fontSize: "0.9rem" }}>{vol.signal === null ? "no signal" : vol.signal.toFixed(4)}</div>
                    <div className="note" style={{ fontSize: "0.68rem" }}>rank-space, not a real %</div>
                  </div>
                )}
              </div>
              {ret.secondary_horizons.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div className="note" style={{ fontWeight: 700, marginBottom: 6 }}>Other horizons with signal</div>
                  {ret.secondary_horizons.map((h) => (
                    <div key={h.horizon} style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--surface-2)", border: "1px solid var(--rule)", borderRadius: 8, padding: "10px 14px", marginBottom: 6 }}>
                      <div>
                        <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, color: h.signal >= 0 ? "var(--ok)" : "var(--bad)" }}>
                          {h.signal >= 0 ? "+" : ""}{(h.signal * 100).toFixed(2)}%
                        </div>
                        <div className="mono note">{h.horizon}d horizon · {Object.keys(h.contributing_segments).join(", ")}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <ConvictionExplain
                conviction={ret.conviction}
                primaryHorizon={ret.matched_horizon}
                primarySignal={ret.signal}
                secondary={ret.secondary_horizons.map((h) => ({ horizon: h.horizon, signal: h.signal }))}
              />
              <div className="hint" style={{ marginTop: 10 }}>Computed on the fly — no stored per-stock object. To hand-tune this specific stock, use the Simulator.</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---- GAPS ----

function GapsTab({ token, authAware, setError }: { token: string; authAware: AuthAware; setError: SetError }) {
  const [data, setData] = useState<CoverageGapsDto | null>(null);
  useEffect(() => {
    fetchCoverageGaps(token).then(setData).catch((e) => { if (!authAware(e)) setError(e instanceof ApiError ? e.message : "failed to load gaps"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const { sorted: sortedGaps, sortKey, sortDir, toggleSort } = useSort<SegmentGridRowDto>(
    data?.gaps_by_impact ?? [], (row, key) => (row as unknown as Record<string, unknown>)[key], "n_constituents",
  );

  if (!data) return <div className="empty-state">Loading…</div>;

  return (
    <>
      <div className="card" style={{ padding: 0 }}>
        <div className="h3" style={{ padding: "16px 16px 0" }}>
          <span>Coverage gaps <FieldHelp term="segment_gap_impact" /> <span className="note">no champion model · ranked by stocks deepened if built</span></span>
          <button className="btn ghost sm" onClick={() => exportRowsAsCsv(
            data.gaps_by_impact as unknown as Record<string, unknown>[],
            [{ key: "label", label: "Segment" }, { key: "dimension", label: "Dimension" }, { key: "n_constituents", label: "Stocks deepened" }],
            "coverage_gaps.csv",
          )}>↓ CSV</button>
        </div>
        {sortedGaps.length === 0 ? (
          <div className="empty-state">
            {data.tested_no_signal.length > 0
              ? "No genuine build gaps — every segment without a champion has already been tested and confirmed no signal (see the table below)."
              : "No gaps — every active segment has a champion model."}
          </div>
        ) : (
          <table className="stocks-table">
            <thead>
              <tr>
                <th className="l">If you build…</th>
                <th className="l clickable" onClick={() => toggleSort("dimension")}>Slot {sortGlyph("dimension", sortKey, sortDir)}</th>
                <th className="r clickable" onClick={() => toggleSort("n_constituents")}>Stocks deepened {sortGlyph("n_constituents", sortKey, sortDir)}</th>
              </tr>
            </thead>
            <tbody>
              {sortedGaps.map((g) => (
                <tr key={g.segment_key}>
                  <td className="l" style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>{g.label ?? g.segment_key} model</td>
                  <td className="l mono" style={{ fontSize: "0.78rem", color: "var(--ink-soft)" }}>{g.segment_key}</td>
                  <td className="r mono" style={{ color: "var(--ok)", fontWeight: 600 }}>+{g.n_constituents.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data.tested_no_signal.length > 0 && (
        <div className="card" style={{ padding: 0 }}>
          <div className="h3" style={{ padding: "16px 16px 0" }}>
            <span>Tested — no directional signal <FieldHelp term="tested_no_signal" /> <span className="note">a real build attempt was made and confirmed no signal — resolved, NOT a build target</span></span>
          </div>
          <table className="stocks-table">
            <thead>
              <tr>
                <th className="l">Segment</th>
                <th className="l">Slot</th>
                <th className="r">Stocks affected</th>
                <th className="l">Why</th>
              </tr>
            </thead>
            <tbody>
              {data.tested_no_signal.map((g) => (
                <tr key={g.segment_key}>
                  <td className="l" style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>{g.label ?? g.segment_key}</td>
                  <td className="l mono" style={{ fontSize: "0.78rem", color: "var(--ink-soft)" }}>{g.segment_key}</td>
                  <td className="r mono">{g.n_constituents.toLocaleString()}</td>
                  <td className="l note" style={{ fontSize: "0.78rem" }}>{g.no_signal_reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="foot-note">These segments are NOT build opportunities — the model factory already tried, at full rigour, and confirmed no forecastable signal exists. Kept separate from the impact-ranked gaps above so an admin is never told to build something already refused.</div>
        </div>
      )}
      <div className="card" style={{ padding: 0 }}>
        <div className="h3" style={{ padding: "16px 16px 0" }}>
          <span>Thinly-covered stocks <FieldHelp term="stock_depth" /> <span className="note">served by only 1 model today · showing {data.shown_thinly_covered} of {data.total_thinly_covered}</span></span>
        </div>
        <div className="table-wrap" style={{ maxHeight: 420, overflow: "auto" }}>
          <table className="stocks-table">
            <thead><tr><th className="l">Stock</th><th className="l">Only model</th><th className="l">Missing depth on</th></tr></thead>
            <tbody>
              {data.thinly_covered.map((t) => (
                <tr key={t.security_id}>
                  <td className="l" style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>{t.display_ticker ?? t.security_id}</td>
                  <td className="l mono" style={{ fontSize: "0.78rem" }}>{t.only_model}</td>
                  <td className="l note">{t.missing_dimensions.join(" · ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ---- RECLASSIFICATIONS ----

function ReclassificationsTab({ token, authAware, setError }: { token: string; authAware: AuthAware; setError: SetError }) {
  const [rows, setRows] = useState<ReclassificationRowDto[] | null>(null);
  useEffect(() => {
    fetchCoverageReclassifications(token, 200).then(setRows).catch((e) => { if (!authAware(e)) setError(e instanceof ApiError ? e.message : "failed to load reclassifications"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const { sorted, sortKey, sortDir, toggleSort } = useSort<ReclassificationRowDto>(
    rows ?? [], (row, key) => (row as unknown as Record<string, unknown>)[key], "changed_on",
  );

  if (!rows) return <div className="empty-state">Loading…</div>;

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="h3" style={{ padding: "16px 16px 0" }}>
        <span>Recent reclassifications <span className="note">membership drifts as-of-T · a tier change moves which model scores a stock — surfaced, not silent</span></span>
        <button className="btn ghost sm" onClick={() => exportRowsAsCsv(
          rows as unknown as Record<string, unknown>[],
          [
            { key: "display_ticker", label: "Stock" }, { key: "dimension", label: "Dimension" },
            { key: "from_segment", label: "From" }, { key: "to_segment", label: "To" },
            { key: "changed_on", label: "Changed on" }, { key: "consequence", label: "Consequence" },
          ], "coverage_reclassifications.csv",
        )}>↓ CSV</button>
      </div>
      {rows.length === 0 ? (
        <div className="empty-state">No reclassifications recorded — genuinely empty, not a loading state.</div>
      ) : (
        <table className="stocks-table">
          <thead>
            <tr>
              <th className="l clickable" onClick={() => toggleSort("display_ticker")}>Stock {sortGlyph("display_ticker", sortKey, sortDir)}</th>
              <th className="clickable" onClick={() => toggleSort("dimension")}>Dimension {sortGlyph("dimension", sortKey, sortDir)}</th>
              <th className="l">Change</th>
              <th className="l">Consequence</th>
              <th className="r clickable" onClick={() => toggleSort("changed_on")}>Changed on {sortGlyph("changed_on", sortKey, sortDir)}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => (
              <tr key={i}>
                <td className="l" style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>{r.display_ticker ?? r.security_id}</td>
                <td><span className="chip">{r.dimension}</span></td>
                <td className="l mono" style={{ fontSize: "0.82rem" }}>{r.from_segment} <span className="note">→</span> <b style={{ color: "var(--accent)" }}>{r.to_segment}</b></td>
                <td className="l note" style={{ fontSize: "0.8rem" }}>{r.consequence}</td>
                <td className="r mono" style={{ fontSize: "0.78rem" }}>{r.changed_on}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
